/**
 * Glue layer for Roulette. Mirrors the imperative side-effect orchestration
 * in `_GameScreenState` of
 *   packages/amgroulette_v2/lib/presentation/screens/game_screen.dart
 * plus `_onConfirmBets` / `_handleMultiplayerStartPlay` /
 * `_startResultSequence` in game_bloc.dart.
 *
 * Roulette flow differs from Dragon Tiger:
 *   - There is NO card reveal sequence — the "reveal" is the wheel spin
 *     driven entirely by Rive (RouletteTrigger + ResultTrigger).
 *   - One winning number per round (0..36); chips settle by whether their
 *     bet area covers that number.
 *
 * Input flow (driven by betting Rive events):
 *   placeBetClick {betID}      → tryPlaceBet(betID)
 *   evtClickChips {chipsAmt}   → selectChip(i)
 *   evtClearClick              → clearAll
 *   evtButtonPlayClick         → confirm
 *   evt36Bet                   → setActiveBettingLayout('normal')
 *   evtCrossBet                → setActiveBettingLayout('cross')
 *
 * Output flow:
 *   GamePhase.betting           → fireBetPanel(1) + girlAnim(1)
 *   confirm → REST /bet         → fireBetPanel(2) + girlAnim(2) + spinWheel(winningNumber)
 *   Rive evtBallStop            → fireResultPanel(n) + animateAllToCenter
 *   Rive evtResultStop           → settle chips + showWinLose + new round
 */
import { DEFAULT_GAME_CONFIG, GAME_KEY_MULTI, GAME_KEY_SINGLE, DEFAULT_AGENT_ID, DEFAULT_PASSWORD, WHEEL_SPIN_FALLBACK_MS, RESULT_STOP_FALLBACK_MS } from '../config';
import { betCovers, isRed, BET_ID_INFO, type GameRound } from '../types/game';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot, BetType } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// The launch-time mode dialog is no longer shown — the mode is derived from the
// URL (see docs/DRAGONTIGER_PIXI_GAMEMODE_FROM_LAUNCH_URL.md). The dialog impl is
// kept and still importable; only `GameMode` (type) is used now, so the value
// import is reduced to type-only to satisfy noUnusedLocals.
// import { showModeSelectionDialog, type GameMode } from '../ui/modeSelectionDialog';
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import { parseResultString } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  params: UrlParams;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid = '';

  // Round-sequence guard flags. Reset at the start of every new round.
  private didStartSpin = false;
  private didStartResultPanel = false;
  private didStartSettlement = false;
  private didShowWinLose = false;
  // The 1.5 s win/lose reveal point inside startSettlement has been reached.
  // Gates the multiplayer Result backstop (handleResult) so a late settlement
  // can still drive the popup after the local reveal sequence has run.
  private endingRevealDone = false;
  // Game-Log snapshot. In multiplayer the settlement Result can land after
  // endRound has cleared currentRound + the bets, so the bets + round are
  // captured at StartPlay and the log entry is built from this snapshot in
  // handleResult. didLogRound makes the write idempotent per round.
  private pendingLogRound: GameRound | null = null;
  private pendingLogBetTotal = 0;
  private pendingLogBetDetails = '';
  private didLogRound = false;
  // Balance settlement is deferred to the WIN/LOSE reveal (the t=1.5s timer
  // in startSettlement) so the displayed (available) balance stays "bet
  // deducted" through the wheel spin and only reflects winnings once the
  // result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped at every round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round (retransmitted Result must not
  // recompute against the already-settled wallet / double-log).
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  // Multiplayer.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode is derived from the launch URL (the `g` game key + path), so there
      // is no launch-time mode dialog. The original dialog block is kept verbatim
      // (commented) so it can be re-enabled — see
      // docs/DRAGONTIGER_PIXI_GAMEMODE_FROM_LAUNCH_URL.md.
      //
      //   this.d.state.setPhase('modeSelection');
      //   this.d.hud.setStatus('Select mode');
      //   const mode: GameMode = await showModeSelectionDialog();
      //   debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  private applyServerConfig(chips: number[], minBet: number, maxBet: number): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id === 'number' && Number.isFinite(id)) {
        this.tryPlaceBet(id);
      }
    });

    this.d.rive.on('evtClickChips', (payload) => {
      // Different .riv builds use different property names.
      const i = (payload.chipsAmt ?? payload.chipIndex ?? payload.index) as number | undefined;
      if (typeof i === 'number') this.d.state.selectChip(i);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());
    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());

    this.d.rive.on('evt36Bet', () => {
      this.d.state.setBetType('normal');
      this.d.rive.setActiveBettingLayout('normal');
    });
    this.d.rive.on('evtCrossBet', () => {
      this.d.state.setBetType('cross');
      this.d.rive.setActiveBettingLayout('cross');
    });

    // Wheel finished spinning — show the result panel.
    this.d.rive.on('evtBallStop', () => {
      debugLog('[Orchestrator] evtBallStop received');
      this.onBallStop();
    });

    // Result panel animation finished — settle chips + win/lose popup + new round.
    this.d.rive.on('evtResultStop', () => {
      debugLog('[Orchestrator] evtResultStop received');
      this.startSettlement();
    });

    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      // Mirrors mainWinLoseEvent in indexPage.dart:663-676.
      if (parts === 'startBetEnd') {
        this.d.rive.triggerGirlAnim(1);
      } else if (parts === 'noMoreBet_End') {
        this.d.rive.triggerGirlAnim(2);
        this.startSpinIfNeeded();
      }
    });
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(betId: number): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(betId, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', betId, chip);
      return;
    }
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(betId, chip, chipIndex);
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
    this.d.chips.hardClear();
  }

  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }
    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      const totalBet = this.d.state.bets.total;

      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        winningNumber: parsed.winningNumber,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      });
      // Defer the balance update to the WIN/LOSE reveal (the t=1.5s timer in
      // startSettlement) so the displayed balance stays "bet deducted"
      // through the wheel spin and only reflects winnings when the result is
      // shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      // Game-Log entry. balanceBefore is the pre-settlement wallet (the deduct
      // is deferred), so balanceBefore + winLose === afterBalance. Bets are
      // still intact here (cleared at settle), so betDetails is accurate.
      GameLogManager.add({
        betAmount: totalBet,
        betDetails: formatBetDetails(this.d.state.bets),
        winLoseAmount: result.totalWinLose,
        balanceBefore: this.d.state.balance,
        balanceAfter: result.afterBalance,
        gameResult: resultLabel(parsed.winningNumber),
        roundId: this.gid || null,
      });

      // Transition: bettingClosed → spinning, fire NoMoreBet popup, then
      // start the wheel spin (driven by Rive). The orchestrator schedules
      // the spin via startSpinIfNeeded; it's also called from the Rive
      // UIButtonClick `noMoreBet_End` event handler.
      this.d.state.setPhase('spinning');
      this.d.rive.fireBetPanel(2);
      this.d.rive.triggerGirlAnim(2);
      this.d.hud.setStatus('No more bets');

      // Safety-net: if Rive's noMoreBet_End event doesn't fire within ~1s,
      // start the spin ourselves.
      this.scheduleTimer(() => this.startSpinIfNeeded(), 1000);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per INDIVIDUAL chip (not per bet area total): the hub
   * validates each amount against the configured chip denominations and
   * rejects a summed total (e.g. 300) with "Invalid Chip Amount", so we submit
   * each chip separately — the server adds each to the player's stake, which
   * is equivalent to the original per-chip increments. Idempotent: the
   * didSubmitMpBets guard prevents a double-submit when both the countdown
   * tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  /**
   * Begin a new betting round. Clear chip layer, reset guards, fire StartBet
   * popup, prep girl.
   */
  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartSpin = false;
    this.didStartResultPanel = false;
    this.didStartSettlement = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    this.resetRoundLogSnapshot();

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.setBetAmountText('0');

    this.d.rive.fireBetPanel(1);
    this.d.rive.triggerGirlAnim(1);

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Kick off the wheel spin once we know the winning number. Idempotent —
   * called from both the REST confirm path and from Rive's UIButtonClick
   * `noMoreBet_End` event.
   */
  private startSpinIfNeeded(): void {
    if (this.didStartSpin) return;
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartSpin = true;

    debugLog('[Orchestrator] spinning wheel to', round.winningNumber);
    this.d.rive.spinWheel(round.winningNumber);

    // Safety-net: if Rive's evtBallStop never fires, force the result panel
    // ~8 s later.
    this.scheduleTimer(() => this.onBallStop(), WHEEL_SPIN_FALLBACK_MS);
  }

  /**
   * Ball landed in the slot — converge chips on the centre and fire the
   * result panel announcement. Idempotent.
   */
  private onBallStop(): void {
    if (this.didStartResultPanel) return;
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartResultPanel = true;

    this.d.chips.animateAllToCenter();
    this.d.rive.fireResultPanel(round.winningNumber);
    this.d.hud.setStatus(`Result: ${round.winningNumber}`);

    // Safety-net: if Rive's evtResultStop never fires, start settlement
    // anyway after RESULT_STOP_FALLBACK_MS.
    this.scheduleTimer(() => this.startSettlement(), RESULT_STOP_FALLBACK_MS);
  }

  /**
   * Result panel done — settle chips by win/lose, show win/lose popup,
   * advance to new round.
   */
  private startSettlement(): void {
    if (this.didStartSettlement) return;
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartSettlement = true;
    this.cancelTimers();

    this.d.state.setPhase('showResult');
    this.d.chips.settle(round.winningNumber);

    // t=1.5s — win/lose popup (if there was any wagering).
    this.scheduleTimer(() => {
      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();
      // Read the win/lose FRESH from currentRound — in multiplayer the
      // settlement Result patches the real figure in via applyRoundResult
      // (which REPLACES currentRound) and can land in the 1.5 s gap before this
      // callback; the captured `round` would still hold the StartPlay
      // placeholder (totalWinLose 0). Falls back to the captured round for
      // single-player, where confirm() put the figure on currentRound up front.
      const display = this.displayWinLoseFor(this.d.state.currentRound ?? round);
      // The reveal point is reached now — flag it so a late multiplayer Result
      // can drive the popup as a backstop (see handleResult / revealWinLose). In
      // single-player (and multiplayer when the Result already arrived)
      // currentRound holds the real figure, so this shows immediately; otherwise
      // display is 0 here and the backstop reveals it once the Result lands.
      this.endingRevealDone = true;
      this.revealWinLose(display);
    }, 1500);

    // t=5s — wrap the round.
    this.scheduleTimer(() => this.endRound(), 5000);
  }

  /**
   * The win/lose figure to DISPLAY for a round — mirrors the Flutter game's
   * displayWinLose: the GROSS winnings when the player won any bet area (even
   * if the net is a loss, so it still reads "YOU WIN"), otherwise the net loss,
   * otherwise 0 (no bet / push). Positive = win, negative = loss, 0 = neither.
   */
  private displayWinLoseFor(round: GameRound | null): number {
    if (!round) return 0;
    if (round.positiveWinAmount > 0) return round.positiveWinAmount; // won an area → WIN (gross)
    // Safety net: a net win necessarily implies a winning row, so this only
    // fires if the per-row breakdown was missing/empty — never hide a win.
    if (round.totalWinLose > 0) return round.totalWinLose;
    return round.totalWinLose < 0 ? round.totalWinLose : 0;          // net loss, else nothing
  }

  /**
   * Show the YOU WIN / YOU LOSE popup on the MainWinLose overlay exactly once
   * per round. `display` follows displayWinLoseFor: positive = win (the gross
   * winnings — fires whenever the player won ANY area, even on a net loss),
   * negative = lose (the net loss), 0 = nothing (push / no bet → no popup).
   * Idempotent via didShowWinLose; a 0 leaves the guard UNSET so a later caller
   * with the real figure (the multiplayer Result backstop) can still reveal it.
   *
   * Driven by an EXPLICIT amount rather than reading currentRound here, because
   * in multiplayer the authoritative settlement Result can land after the local
   * reveal sequence — the popup must use the amount extracted in handleResult,
   * not the (by-then possibly stale) round.
   */
  private revealWinLose(display: number): void {
    if (this.didShowWinLose) return;
    if (display === 0) return;
    this.didShowWinLose = true;
    this.d.rive.showWinLose(display > 0, formatCurrency(Math.abs(display)));
    this.d.hud.setStatus(
      display > 0
        ? `YOU WIN ${formatCurrency(display)}`
        : `YOU LOSE ${formatCurrency(-display)}`,
    );
    this.d.state.setPhase('result');
  }

  /** Clear the per-round Game-Log snapshot (reset at every round start). */
  private resetRoundLogSnapshot(): void {
    this.pendingLogRound = null;
    this.pendingLogBetTotal = 0;
    this.pendingLogBetDetails = '';
    this.didLogRound = false;
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the t=1.5s win/lose timer and again from endRound. If the
   * server balance isn't known yet (a late multiplayer Result), leaves the
   * bets in place; handleResult finalizes the moment the balance arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant
      // the balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * End-of-round teardown — clear chips + advance to next round in
   * single-player. In multiplayer, the hub's StartGame event drives this.
   */
  private endRound(): void {
    this.cancelTimers();
    // Safety net: ensure the balance was settled even if the t=1.5s win/lose
    // timer was skipped (e.g. a no-bet round short-circuits straight here).
    this.applySettlement();
    this.d.chips.hardClear();
    if (this.d.state.currentRound) {
      this.d.rive.setLatestHistory(this.d.state.currentRound.winningNumber);
    }
    if (!this.d.state.isMultiplayer) {
      this.startBettingRound();
    } else {
      this.d.state.setPhase('newRound');
    }
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      this.d.rive.setBetAmountText(formatCurrency(next.bets.total));
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.betType !== prev.betType) {
      this.d.rive.setActiveBettingLayout(next.betType === 'normal' ? 'normal' : 'cross');
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether chip / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: the hub's StartBet/EndBet and our
   * countdown-end submit gate it via `multiplayerCanBet`.
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.isMultiplayer ? s.multiplayerCanBet : s.phase === 'betting';
  }

  // =================================================================
  // Multiplayer
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame's sessionId is the URL-DECODED bearer token, NOT the gsid.
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }
    if (env && String(env.State ?? '').toLowerCase() === 'restart') {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;
      case 'StartGame':
        this.handleStartGame(env);
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet(env);
        break;
      case 'StartPlay':
        this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;
      case 'GetMessages':
        break;
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      if (chips.length > 0) {
        this.d.state.setConfig({
          ...this.d.state.config,
          chipValues: chips,
        });
      }
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  private handleStartGame(_env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.cancelTimers();

    this.roundToken++;
    this.didStartSpin = false;
    this.didStartResultPanel = false;
    this.didStartSettlement = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    this.resetRoundLogSnapshot();

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('newRound');
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    this.d.rive.fireBetPanel(1);
    this.d.rive.triggerGirlAnim(1);
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.rive.fireBetPanel(2);
    this.d.hud.setStatus('No more bets');
  }

  /**
   * StartPlay — cards/wheel data arrives in `Result` field (same CSV format
   * as single-player /bet response). Drive the spin.
   */
  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    const round: GameRound = {
      roundId: '',
      nextRoundId: '',
      winningNumber: parsed.winningNumber,
      // WinLose isn't known yet — comes via the Result event.
      totalWinLose: 0,
      totalBet: this.d.state.bets.total,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    };
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.applyRoundResult(round);

    // Snapshot the bets NOW for the Game-Log — the settlement Result can land
    // after endRound has cleared currentRound + the bets, so the entry is built
    // from this snapshot in handleResult rather than from live state.
    this.pendingLogRound = round;
    this.pendingLogBetTotal = round.totalBet;
    this.pendingLogBetDetails = formatBetDetails(this.d.state.bets);

    this.d.state.setPhase('spinning');
    this.startSpinIfNeeded();
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // The Rive evtResultStop event will drive startSettlement; if we got
    // here before settlement started (and the result panel is already up),
    // kick it off now so the popup appears. Purely cosmetic — must NOT wait on
    // the balance round-trip below.
    if (!this.didStartSettlement && this.didStartResultPanel) {
      this.startSettlement();
    }

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance on a loss, so the wallet
    // can't be derived locally — we read it from the server (/action/balance).
    // Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event on a loss AND returns WinLost=0 even on a loss, so neither
   *      the wallet nor the net P&L can be derived locally; the only correct
   *      source is the server's own balance. NOTE: /action/getinfo does NOT carry
   *      a balance (config only), so we must use /action/balance — the same
   *      endpoint the startup balance fetch uses.
   *
   * Async because of the /action/balance round-trip, so it captures the round
   * token up front and bails if a new round started before the balance arrived —
   * a slow fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    // Gross winnings from the user's WINNING bet rows — drives the YOU WIN popup
    // even on a net loss. Extracted here (env in scope) and threaded through to
    // applyResultSettlement.
    const positiveWin = extractPositiveWinForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, positiveWin, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, drive the
   * YOU WIN / YOU LOSE popup, and log the round. `balance` is the authoritative
   * post-round wallet (from the Result event or /action/balance); null only when
   * both sources failed, in which case we fall back to `balanceBefore + winLose`
   * (the legacy local computation). Once per round (didApplyResultSettlement) — a
   * retransmitted settlement Result must not recompute against the already-settled
   * wallet or double-log.
   */
  private applyResultSettlement(
    winLose: number,
    positiveWin: number,
    balance: number | null,
  ): void {
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    // Capture BEFORE any settle()/setBalance() below mutates the wallet. The
    // pre-settlement wallet is the pre-bet wallet (per-bet acks are no-ops), so
    // balanceBefore + winLose === balanceAfter — used by the Game-Log entry.
    const balanceBefore = this.d.state.balance;

    this.d.state.setMultiplayerWinLose(winLose);
    // Authoritative post-round balance. When the server omits it (loss), fall
    // back to the legacy local reconstruction.
    const balanceAfter = balance ?? balanceBefore + winLose;
    this.pendingSettleBalance = balanceAfter;
    if (this.awaitingSettleBalance && !this.didSettle) {
      // The reveal already reached the settle point and was only waiting on
      // this balance — finalize now (atomic balance update + bet clear).
      // Settling atomically here avoids clearing bets early (which would
      // flash the pre-settlement wallet).
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    } else if (this.didSettle) {
      // Late Result after settlement already applied — sync the wallet so
      // the server's value isn't lost.
      this.d.state.setBalance(balanceAfter);
    }

    // Patch currentRound's win/lose so a not-yet-run reveal (the 1.5 s timer in
    // startSettlement) reads the real figure. applyRoundResult REPLACES
    // currentRound, so the captured `round` in that timer would otherwise stay
    // at the StartPlay placeholder (totalWinLose 0) — which is exactly why the
    // multiplayer popup never showed.
    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: positiveWin,
        hasPositiveWin: positiveWin > 0,
      });
    }

    // Drive the YOU WIN / YOU LOSE popup from the AUTHORITATIVE settlement
    // figures — independent of currentRound. In multiplayer the Result routinely
    // lands AFTER the local 1.5 s reveal has already run (and found no figure
    // yet), so reveal here once that point has passed. If the Result arrives
    // BEFORE the reveal, currentRound is patched above and the reveal timer shows
    // it instead. Idempotent via didShowWinLose.
    const resultDisplay = positiveWin > 0 ? positiveWin : winLose < 0 ? winLose : 0;
    if (this.endingRevealDone) {
      this.revealWinLose(resultDisplay);
    }

    // Record the Game-Log entry from the StartPlay snapshot (bets) + the
    // settlement figures — NOT live state, which endRound may already have
    // cleared if the Result landed late. Gated by didLogRound so a duplicate /
    // late Result can't double-log.
    const logRound = this.pendingLogRound;
    if (logRound && !this.didLogRound) {
      this.didLogRound = true;
      GameLogManager.add({
        betAmount: this.pendingLogBetTotal,
        betDetails: this.pendingLogBetDetails,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter,
        gameResult: resultLabel(logRound.winningNumber),
        roundId: logRound.roundId || null,
      });
    }
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  // -----------------------------------------------------------------
  // Countdowns
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status =
            typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// Silence unused-import warning until tooling consumes BetType
export const _UnusedBetType = (_: BetType): void => {
  /* type-only re-export bridge */
};
// Silence unused betCovers re-export.
export const _unusedBetCovers = betCovers;

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

// Friendly labels for the grouped bets whose covered-numbers list would be too
// long to show in the Game-Log (columns/dozens/even-money). Column ids map per
// BET_ID_INFO's visual-row order: 198=bottom(Col3), 199=middle(Col2), 200=top(Col1).
const NAMED_BETS: Record<number, string> = {
  198: 'Col3', 199: 'Col2', 200: 'Col1',
  201: '1st12', 202: '2nd12', 203: '3rd12',
  204: '1-18', 205: '19-36',
  206: 'Red', 207: 'Black',
  208: 'Odd', 209: 'Even',
};

/** Short human label for a roulette bet area (e.g. "17", "1/2", "1-6", "Red"). */
function labelForBet(betId: number): string {
  const named = NAMED_BETS[betId];
  if (named) return named;
  const info = BET_ID_INFO.get(betId);
  if (!info) return `#${betId}`;
  if (info.category === 'sixline') {
    const ns = info.numbers;
    return `${ns[0]}-${ns[ns.length - 1]}`;
  }
  return info.numbers.join('/');
}

/** "area:amount, area:amount, …" for the Game-Log middle row. */
function formatBetDetails(bets: {
  entriesList(): ReadonlyArray<{ betId: number; amount: number }>;
}): string {
  const parts: string[] = [];
  for (const e of bets.entriesList()) {
    if (e.amount > 0) parts.push(`${labelForBet(e.betId)}:${formatCurrency(e.amount)}`);
  }
  return parts.join(', ');
}

/** Game-Log result text: "Result: 17 (Red)" / "Result: 0 (Green)". */
function resultLabel(winningNumber: number): string {
  if (winningNumber === 0) return 'Result: 0 (Green)';
  return `Result: ${winningNumber} (${isRed(winningNumber) ? 'Red' : 'Black'})`;
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

// =====================================================================
// Multiplayer envelope helpers
// =====================================================================

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }
  return 0;
}

/**
 * Sum ONLY the current user's POSITIVE WinLose rows out of the Data array — the
 * gross winnings from the bet areas that WON. Mirrors the Flutter game's
 * positiveForUser (_computeMultiplayerWinLoseSummary). Used so the YOU WIN popup
 * shows whenever the player won any area, even if their net result is a loss.
 * Same row-matching rules as extractWinLoseForUser (user rows only).
 */
function extractPositiveWinForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const v = asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
      if (v > 0) sum += v;
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    const v = asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
    return v > 0 ? v : 0;
  }
  return 0;
}

function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;
  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const b = item.Balance ?? item.balance;
      if (b == null) return null;
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null;
    const b = m.Balance ?? m.balance;
    if (b == null) return null;
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pickUserName(item: Record<string, unknown>): string {
  const raw =
    item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}
